// StatusPillButton — clickable contract status pill that opens a popover
// listing locked transitions for the current state. For reason-required
// transitions (draft→archived, active→terminated, active→archived) the
// popover swaps to a reason-capture form. Terminal states render a plain
// pill with no chevron and no popover. Single source of truth for which
// transitions are available comes from contractStatusTransitions lib.

import { useEffect, useRef, useState } from "react";
import {
  availableTransitions,
  isTerminalStatus,
  transitionRequiresReason,
  type ContractStatusTransition,
} from "../../../lib/contract-overview/contractStatusTransitions";
import type { ManagedContractStatus } from "../../../types";
import {
  CONTRACT_STATUS_LABEL as STATUS_LABELS,
  CONTRACT_STATUS_PILL_CLASS as PILL_VARIANTS,
} from "../../../lib/contract-overview/contractStatusPill";

interface StatusPillButtonProps {
  status: ManagedContractStatus;
  onChange: (
    newStatus: ManagedContractStatus,
    reason?: string,
  ) => void;
  isLoading?: boolean;
}


export function StatusPillButton({
  status,
  onChange,
  isLoading,
}: StatusPillButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [reasonFor, setReasonFor] =
    useState<ContractStatusTransition | null>(null);
  const [reason, setReason] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const terminal = isTerminalStatus(status);
  const transitions = availableTransitions(status);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
        setReasonFor(null);
        setReason("");
        setShowMore(false);
        buttonRef.current?.focus();
      }
    }
    function onClickOutside(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setReasonFor(null);
        setReason("");
        setShowMore(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [isOpen]);

  const handlePillClick = () => {
    if (terminal || isLoading) return;
    setIsOpen((v) => !v);
  };

  const handleTransitionClick = (t: ContractStatusTransition) => {
    if (transitionRequiresReason(status, t.to)) {
      setReasonFor(t);
      return;
    }
    setIsOpen(false);
    setShowMore(false);
    onChange(t.to, undefined);
  };

  const handleConfirmReason = () => {
    if (!reasonFor) return;
    if (!reason.trim()) return;
    const target = reasonFor.to;
    setIsOpen(false);
    setReasonFor(null);
    setReason("");
    setShowMore(false);
    onChange(target, reason.trim());
  };

  const handleCancelReason = () => {
    setReasonFor(null);
    setReason("");
  };

  const primary = transitions.filter((t) => t.group === "primary");
  const moreOptions = transitions.filter((t) => t.group === "moreOptions");

  const ariaLabel = terminal
    ? `Contract status: ${STATUS_LABELS[status]}`
    : `Contract status: ${STATUS_LABELS[status]}. Click to change status.`;

  return (
    <span className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={handlePillClick}
        aria-label={ariaLabel}
        aria-haspopup={terminal ? undefined : "menu"}
        aria-expanded={terminal ? undefined : isOpen}
        disabled={isLoading}
        className={`inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-1 rounded-full text-caption font-medium ${
          PILL_VARIANTS[status]
        } ${
          terminal
            ? "cursor-default"
            : "cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        } ${isLoading ? "opacity-60 cursor-wait" : ""}`}
      >
        {STATUS_LABELS[status]}
        {!terminal && (
          <span
            data-testid="status-pill-chevron"
            aria-hidden="true"
            className="text-[10px] ml-0.5"
          >
            ▾
          </span>
        )}
      </button>

      {isOpen && !terminal && (
        <div
          ref={popoverRef}
          role="menu"
          className="absolute left-0 mt-1.5 min-w-[280px] rounded-lg shadow-soft-lg dark:shadow-soft-lg-dark p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 z-50"
        >
          {reasonFor ? (
            <div className="space-y-2">
              <label
                htmlFor="status-pill-reason"
                className="block text-body-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Reason for change to {reasonFor.label}
              </label>
              <textarea
                id="status-pill-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="input min-h-[80px] w-full"
                placeholder="Provide a reason for this status change..."
                maxLength={500}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancelReason}
                  className="btn-secondary text-body-sm py-1 px-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReason}
                  disabled={!reason.trim()}
                  className="btn-primary text-body-sm py-1 px-2 disabled:opacity-60"
                >
                  Confirm
                </button>
              </div>
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {primary.map((t) => (
                <li key={t.to}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleTransitionClick(t)}
                    className="w-full text-left text-body-sm py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                  >
                    {t.label}
                    {t.requiresReason && (
                      <>
                        {" "}
                        <span className="text-caption text-slate-500 dark:text-slate-400">
                          (reason required)
                        </span>
                      </>
                    )}
                  </button>
                </li>
              ))}
              {moreOptions.length > 0 && !showMore && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowMore(true)}
                    className="w-full text-left text-caption py-1.5 px-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  >
                    More options ▾
                  </button>
                </li>
              )}
              {moreOptions.length > 0 &&
                showMore &&
                moreOptions.map((t) => (
                  <li key={t.to}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => handleTransitionClick(t)}
                      className="w-full text-left text-body-sm py-1.5 px-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    >
                      {t.label}{" "}
                      <span className="text-caption text-slate-500 dark:text-slate-400">
                        (reason required)
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

export default StatusPillButton;
